import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Stepper,
  Step,
  StepLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { WhatsApp as WhatsAppIcon, Email as EmailIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Estado para recuperacao de senha
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState(0); // 0: email, 1: codigo, 2: concluido
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState('');
  const [maskedContact, setMaskedContact] = useState('');
  const [sendMethod, setSendMethod] = useState('whatsapp'); // whatsapp ou email

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      navigate((typeof window !== 'undefined' && window.location.hostname.startsWith('admin.otimizapp.')) ? '/hub/dashboard' : '/atendimento');
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', {
        email: forgotEmail,
        method: sendMethod
      });
      if (res.data.success) {
        setMaskedContact(res.data.maskedPhone || res.data.maskedEmail || '');
        setSuccess(res.data.message);
        setForgotStep(1);
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao solicitar recuperacao');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError('');
    setSuccess('');
    if (newPassword !== confirmPassword) {
      setError('As senhas nao conferem');
      return;
    }
    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/reset-password', {
        email: forgotEmail,
        code: resetCode,
        newPassword: newPassword
      });
      if (res.data.success) {
        setSuccess('Senha alterada com sucesso! Voce ja pode fazer login.');
        setForgotStep(2);
        // Voltar para login apos 3 segundos
        setTimeout(() => {
          resetForgotMode();
        }, 3000);
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  const resetForgotMode = () => {
    setForgotMode(false);
    setForgotStep(0);
    setForgotEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setMaskedContact('');
    setSendMethod('whatsapp');
  };

  // Modo de recuperacao de senha
  if (forgotMode) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        }}
      >
        <Card sx={{ maxWidth: 450, width: '100%', mx: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
              Recuperar Senha
            </Typography>

            <Stepper activeStep={forgotStep} sx={{ mb: 3 }}>
              <Step><StepLabel>Email</StepLabel></Step>
              <Step><StepLabel>Codigo</StepLabel></Step>
              <Step><StepLabel>Concluido</StepLabel></Step>
            </Stepper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {forgotStep === 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Digite seu email cadastrado e escolha como deseja receber o codigo de recuperacao.
                </Typography>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                />

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Enviar codigo via:
                </Typography>
                <ToggleButtonGroup
                  value={sendMethod}
                  exclusive
                  onChange={(e, v) => v && setSendMethod(v)}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="whatsapp" sx={{ py: 1.5 }}>
                    <WhatsAppIcon sx={{ mr: 1, color: sendMethod === 'whatsapp' ? '#25D366' : 'inherit' }} />
                    WhatsApp
                  </ToggleButton>
                  <ToggleButton value="email" sx={{ py: 1.5 }}>
                    <EmailIcon sx={{ mr: 1, color: sendMethod === 'email' ? '#1976d2' : 'inherit' }} />
                    Email
                  </ToggleButton>
                </ToggleButtonGroup>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleForgotPassword}
                  disabled={loading || !forgotEmail}
                  sx={{ mt: 1 }}
                  size="large"
                >
                  {loading ? <CircularProgress size={24} /> : 'Enviar Codigo'}
                </Button>
              </>
            )}

            {forgotStep === 1 && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Codigo enviado para {maskedContact || (sendMethod === 'whatsapp' ? 'seu WhatsApp' : 'seu email')}
                </Alert>
                <TextField
                  fullWidth
                  label="Codigo de 6 digitos"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  margin="normal"
                  required
                  inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 20, letterSpacing: 6 } }}
                  autoFocus
                />
                <TextField
                  fullWidth
                  label="Nova Senha"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal"
                  required
                  helperText="Minimo 6 caracteres"
                />
                <TextField
                  fullWidth
                  label="Confirmar Nova Senha"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  required
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleResetPassword}
                  disabled={loading || !resetCode || !newPassword || !confirmPassword}
                  sx={{ mt: 2 }}
                  size="large"
                >
                  {loading ? <CircularProgress size={24} /> : 'Alterar Senha'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => { setForgotStep(0); setError(''); setSuccess(''); }}
                  sx={{ mt: 1 }}
                >
                  Reenviar codigo
                </Button>
              </>
            )}

            {forgotStep === 2 && (
              <Typography variant="body1" align="center" color="success.main" sx={{ py: 3 }}>
                Senha alterada com sucesso! Redirecionando para login...
              </Typography>
            )}

            <Button fullWidth variant="text" onClick={resetForgotMode} sx={{ mt: 2 }}>
              Voltar para Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Modo de login normal
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img src='/logo-otimize.png' alt='Otimize Informatica' style={{ maxWidth: 280, height: 'auto' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Acesse sua conta para continuar
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoFocus
              placeholder="seu@email.com"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Senha"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              placeholder="Digite sua senha"
              InputLabelProps={{ shrink: true }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Entrar'}
            </Button>
          </form>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setForgotMode(true)}
              sx={{ cursor: 'pointer' }}
            >
              Esqueci minha senha
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Login;
